/**
 * =============================================================================
 * Helpers de URL — Painel do Afiliado (multi-tenant por slug)
 * Versão: 1.0.0
 *
 * Descrição:
 *   Centraliza a montagem das URLs públicas de auth do afiliado, permitindo
 *   tanto o formato CANÔNICO por tenant (`/afiliado/<slug>/login`) quanto o
 *   formato LEGADO global (`/afiliado/login`). Quando há um `tenantSlug`
 *   conhecido — via params da rota, querystring ou cache local — usamos o
 *   canônico; caso contrário, caímos no legado de forma transparente.
 *
 *   Também expõe utilitários para persistir/recuperar o último tenantSlug
 *   usado, para que redirecionamentos pós-logout/refresh mantenham a URL
 *   "bonita" do tenant.
 * =============================================================================
 */
import { useParams, useSearchParams } from 'react-router-dom';

/** Chave do localStorage para o último tenantSlug acessado pelo afiliado. */
const LAST_TENANT_SLUG_KEY = 'lastAffiliateTenantSlug';

/** Sanitiza um slug para evitar path-injection (apenas a-z 0-9 - _). */
function sanitizeSlug(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  if (!/^[a-z0-9][a-z0-9\-_]{0,80}$/.test(trimmed)) return null;
  return trimmed;
}

/** Lê o último tenantSlug em cache (ou null). */
export function getLastAffiliateTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sanitizeSlug(window.localStorage.getItem(LAST_TENANT_SLUG_KEY));
  } catch {
    return null;
  }
}

/** Persiste o último tenantSlug acessado pelo afiliado. */
export function setLastAffiliateTenantSlug(slug: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  const safe = sanitizeSlug(slug ?? null);
  try {
    if (safe) window.localStorage.setItem(LAST_TENANT_SLUG_KEY, safe);
    else window.localStorage.removeItem(LAST_TENANT_SLUG_KEY);
  } catch {
    /* noop */
  }
}

// ---------------------------------------------------------------------------
// Builders de path
// ---------------------------------------------------------------------------

/** `/afiliado/<slug>/login` ou `/afiliado/login` (legado). */
export function affiliateLoginPath(slug?: string | null): string {
  const safe = sanitizeSlug(slug);
  return safe ? `/afiliado/${safe}/login` : '/afiliado/login';
}

/** `/afiliado/<slug>/esqueci-senha` ou `/afiliado/esqueci-senha`. */
export function affiliateForgotPath(slug?: string | null): string {
  const safe = sanitizeSlug(slug);
  return safe ? `/afiliado/${safe}/esqueci-senha` : '/afiliado/esqueci-senha';
}

/** `/afiliado/<slug>/redefinir-senha` ou `/afiliado/redefinir-senha`. */
export function affiliateResetPath(slug?: string | null): string {
  const safe = sanitizeSlug(slug);
  return safe ? `/afiliado/${safe}/redefinir-senha` : '/afiliado/redefinir-senha';
}

/** `/afiliado/<slug>/aguardando-aprovacao` ou versão global. */
export function affiliateAwaitingPath(slug?: string | null): string {
  const safe = sanitizeSlug(slug);
  return safe
    ? `/afiliado/${safe}/aguardando-aprovacao`
    : '/afiliado/aguardando-aprovacao';
}

/**
 * `/afiliado/<slug>/cadastro` ou `/afiliado/cadastro` (legado).
 * Aponta para o RESOLVER que descobre o programa padrão do tenant e
 * redireciona para `/afiliado/cadastro/<tenantSlug>/<programSlug>`.
 */
export function affiliateSignupPath(slug?: string | null): string {
  const safe = sanitizeSlug(slug);
  return safe ? `/afiliado/${safe}/cadastro` : '/afiliado/cadastro';
}

// ---------------------------------------------------------------------------
// Hook: resolve o tenantSlug efetivo para a tela atual
// ---------------------------------------------------------------------------

/**
 * Resolve o `tenantSlug` para a página atual.
 *   1) `:tenantSlug` da rota (quando estamos no path canônico).
 *   2) Querystring `?tenant=<slug>` (compatibilidade com links antigos).
 *   3) Último slug em cache local (lembra o tenant entre sessões).
 *   4) `null` → renderiza no formato global/legado.
 */
export function useAffiliateTenantSlug(): string | null {
  const params = useParams<{ tenantSlug?: string }>();
  const [search] = useSearchParams();
  const fromParams = sanitizeSlug(params.tenantSlug);
  if (fromParams) return fromParams;
  const fromQuery = sanitizeSlug(search.get('tenant'));
  if (fromQuery) return fromQuery;
  return getLastAffiliateTenantSlug();
}
